import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, ClipboardCheck, X } from "lucide-react";
import { apiGet, apiPost, unpackList } from "../../../Api/Client.js";

// The loop `drill_this_week` opens and this closes.
//
// The patterns panel has always named exactly one thing to work on. Nothing
// downstream of that number used to exist: no record that a coach actually
// assigned it in a 1:1, no way to check the next few calls for it, no answer
// to "did that work" beyond re-reading the panel and hoping someone
// remembers what was said three weeks ago.
//
// Resolution is never trusted off a stored flag here — every render asks
// `/status/` fresh. Its `since_assignment` block splits the rep's calls at the
// moment of assignment and compares the two sides (patterns.since_assignment).
// It used to show this week's generic status instead, which for a drill
// assigned three days ago was mostly graded on calls from before the 1:1.

const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14, padding: "16px 18px" };
const muted = { color: "var(--c-500)" };

// Did it move after the coach assigned it?
const VERDICT = {
  better: { label: "Getting better", color: "var(--green-600)" },
  holding: { label: "Holding", color: "var(--green-600)" },
  not_yet: { label: "Not moving yet", color: "var(--amber-600)" },
  too_early: { label: "Too early to tell", color: "var(--c-500)" },
  not_tested: { label: "Not tested yet", color: "var(--c-500)" },
};

// Fallback for a server that does not send `since_assignment` yet.
const STATUS_LABEL = {
  chronic: { label: "Still not moving", color: "var(--red-600, #c0392b)" },
  recurring: { label: "Recurring", color: "var(--red-600, #c0392b)" },
  new: { label: "First miss since assigned", color: "var(--blue-600)" },
  watch: { label: "Not enough calls yet", color: "var(--c-500)" },
  improving: { label: "Improving", color: "var(--green-600)" },
  resolved: { label: "Resolved", color: "var(--green-600)" },
};

function AssignmentRow({ row, isManager, onDismissed }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");

  useEffect(() => {
    let cancelled = false;
    apiGet(`/RevOps/CoachingAssignments/${row.id}/status/`)
      .then((data) => !cancelled && setStatus(data))
      .catch(() => {});
    return () => { cancelled = true; };
  }, [row.id]);

  const dismiss = async () => {
    // Dismissing without asking why is how "moved to senior track" and
    // "wrong call, my mistake" both end up recorded as the same blank row —
    // the backend has a `dismissed_reason` field precisely so they don't
    // have to. window.prompt is the whole UI: cancel sends nothing.
    const reason = window.prompt(
      `Why is "${row.parameter_label}" being dismissed? (moved tracks, wrong call, etc. — optional)`,
      "",
    );
    if (reason === null) return;
    setBusy(true);
    setFailure("");
    try {
      await apiPost(`/RevOps/CoachingAssignments/${row.id}/dismiss/`, { reason });
      onDismissed && onDismissed(row.id);
    } catch (err) {
      setFailure(err?.payload?.detail || err?.message || "Could not dismiss this assignment.");
    } finally {
      setBusy(false);
    }
  };

  const since = status?.since_assignment || null;
  const spec = since ? VERDICT[since.verdict] : (status && STATUS_LABEL[status.status]);

  return (
    <div style={{ padding: "9px 0", borderTop: "1px solid var(--c-200)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--c-900)" }}>{row.parameter_label}</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {spec ? (
            <span style={{ fontSize: 11, fontWeight: 700, color: spec.color }}>{spec.label}</span>
          ) : (
            <span style={{ fontSize: 11, ...muted }}>—</span>
          )}
          {isManager && (
            <button type="button" onClick={dismiss} disabled={busy} title="Dismiss this assignment"
                    style={{ border: "none", background: "transparent", cursor: "pointer", padding: 2,
                             display: "inline-flex", color: "var(--c-400)" }}>
              <X size={13} />
            </button>
          )}
        </span>
      </div>
      {since?.sentence && (
        <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "var(--c-700)" }}>{since.sentence}</p>
      )}
      {row.note && (
        <p style={{ margin: "3px 0 0", fontSize: 12, ...muted }}>“{row.note}”</p>
      )}
      <div style={{ fontSize: 10.5, ...muted, marginTop: 3 }}>
        Assigned {row.created_at ? row.created_at.slice(0, 10) : "—"}
        {row.assigned_by ? ` by ${row.assigned_by}` : ""}
        {row.target_date ? ` · check back by ${row.target_date}` : ""}
        {!since && status?.opportunities_7d != null
          ? ` · ${status.misses_7d} of ${status.opportunities_7d} calls this week`
          : ""}
      </div>
      {failure && <p role="alert" style={{ margin: "4px 0 0", fontSize: 11.5, color: "var(--red-600)" }}>{failure}</p>}
    </div>
  );
}

// Manager-only: turn the panel's own `drill_this_week` into an assignment
// with one click, rather than a separate form for the one thing a coach
// actually wants to do from here.
function AssignDrillButton({ employeeId, parameterKey, onAssigned }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [open, setOpen] = useState(false);
  const [failure, setFailure] = useState("");

  const assign = async () => {
    setBusy(true);
    setFailure("");
    try {
      const row = await apiPost("/RevOps/CoachingAssignments/", {
        employee: employeeId, parameter_key: parameterKey, note,
        target_date: targetDate || null,
      });
      setOpen(false);
      setNote("");
      setTargetDate("");
      onAssigned && onAssigned(row);
    } catch (err) {
      setFailure(err?.payload?.detail || err?.message || "Could not assign this drill.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
              style={{ display: "inline-flex", alignItems: "center", gap: 5,
                       border: "1px solid var(--c-200)", background: "transparent",
                       borderRadius: 8, padding: "4px 9px", cursor: "pointer",
                       fontSize: 11, fontWeight: 600, color: "var(--blue-600)" }}>
        <ClipboardCheck size={12} /> Assign as this week's drill
      </button>
    );
  }
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      <input value={note} onChange={(e) => setNote(e.target.value)}
             placeholder="What was actually said (optional)"
             style={{ fontSize: 11.5, padding: "5px 8px", borderRadius: 7,
                      border: "1px solid var(--c-200)", background: "var(--surface)",
                      color: "var(--c-900)", flex: 1, minWidth: 160 }} />
      <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)}
             title="Check back by (optional) — a coach may just say 'next call' and leave this blank"
             style={{ fontSize: 11.5, padding: "5px 8px", borderRadius: 7,
                      border: "1px solid var(--c-200)", background: "var(--surface)",
                      color: "var(--c-900)" }} />
      <button type="button" onClick={assign} disabled={busy}
              style={{ border: "none", background: "var(--blue-600)", color: "#fff",
                       borderRadius: 7, padding: "5px 10px", cursor: "pointer",
                       fontSize: 11.5, fontWeight: 600 }}>
        Assign
      </button>
      <button type="button" onClick={() => setOpen(false)}
              style={{ border: "none", background: "transparent", cursor: "pointer",
                       fontSize: 11.5, color: "var(--c-500)" }}>
        Cancel
      </button>
      {failure && <span role="alert" style={{ flexBasis: "100%", fontSize: 11.5, color: "var(--red-600)" }}>{failure}</span>}
    </div>
  );
}

export default function CoachingAssignments({ employeeId, isManager = false,
                                             drillKey = null, drillLabel = "" }) {
  const [rows, setRows] = useState(null);

  // Only the newest request may fill the list, so a slow answer for the previous person
  // never shows under the new one.
  const latestRequest = useRef(0);
  const load = () => {
    latestRequest.current += 1;
    const requestId = latestRequest.current;
    if (!employeeId) { setRows([]); return; }
    const q = isManager ? `?employee=${employeeId}&open=1` : "?open=1";
    apiGet(`/RevOps/CoachingAssignments/${q}`)
      .then((payload) => { if (requestId === latestRequest.current) setRows(unpackList(payload)); })
      .catch(() => { if (requestId === latestRequest.current) setRows([]); });
  };

  useEffect(() => { setRows(null); load(); }, [employeeId, isManager]);   // eslint-disable-line react-hooks/exhaustive-deps

  const alreadyAssigned = drillKey && (rows || []).some((r) => r.parameter_key === drillKey);

  if (rows === null) return null;
  if (!rows.length && !(isManager && drillKey && !alreadyAssigned)) return null;

  return (
    <div style={{ ...card, marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5,
                       fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
                       color: "var(--c-600)" }}>
          <CheckCircle2 size={13} /> {isManager ? "Assignments" : "Assigned to you"}
        </span>
        {isManager && drillKey && !alreadyAssigned && (
          <AssignDrillButton employeeId={employeeId} parameterKey={drillKey} onAssigned={load} />
        )}
      </div>

      {rows.length === 0 ? (
        <p style={{ fontSize: 12, ...muted, margin: "6px 0 0" }}>
          Nothing currently assigned{drillLabel ? ` — "${drillLabel}" is this week's drill, unassigned.` : "."}
        </p>
      ) : (
        rows.map((row) => (
          <AssignmentRow key={row.id} row={row} isManager={isManager}
                         onDismissed={() => setRows((prev) => prev.filter((r) => r.id !== row.id))} />
        ))
      )}
    </div>
  );
}
